/**
 * THE ONE STAGE VOCABULARY (simplification, 2026-09-24).
 *
 *   Proposed → Plan → Build → Review → QA → Done
 *
 * The six stages live in `@yagni/shared` (`pipelineStage.ts`) so the backend
 * list read dates the same stage this surface draws. Every renderer on Work
 * (the row, the case file's spine and timeline, the receipt, the journey) reads
 * them from here and nothing else: the seven-node display spine, the six-step
 * operational rail and the three-beat review journey are gone, and a fourth
 * set must never grow inside a component.
 *
 * Beside the stage, a row carries one of five states (waiting, working,
 * stopped, queued, settled). Stage says where the work stands; state says what
 * is happening to it.
 */
import type { JobActAsk, QaOutcome, WorkItem, WorkPipelineStage } from "@yagni/shared";
import {
  readSplitPlace,
  SPLIT_LABEL,
  splitMergeWords,
  splitPieMode,
  splitProgressWords,
  splitWaitWords,
} from "./splitPlace.js";
import type { StagePieMode } from "../ui/stagePie.js";
import {
  BUILD_STOP_CAUSE_WORDS,
  INFRA_HAND_OFF_SENTENCE,
  isBuildStopCause,
  isFletcherRequestItem,
  isHandOffAskKind,
  isNeedsYou,
  isProposalScopingRef,
  jobActWaitingPhrase,
  liveSplitOf,
  pipelineStageOf,
  PR_CLOSED_UNMERGED_STOP_REASON,
  stageOutcomeOf,
  stopReasonWords,
  WORK_PIPELINE_STAGES,
} from "@yagni/shared";
import {
  acceptedHoldWords,
  outOfRoundsWaitingClause,
  REVIEW_WAIT_UNKNOWN,
  REVIEW_WAIT_WORDS,
} from "./workCopy.js";

export { isFletcherRequestItem, isNeedsYou, pipelineStageOf, stageOutcomeOf };

/**
 * WAITING ON YOU means a gate addressed to you: an open current gate whose
 * supervisor is the reader. The tab count, the masthead, the ask groups, the
 * waiting-first sort and the sidebar pill (`needsYouForViewer`, the same rule
 * server-side) all read this. {@link isNeedsYou} stays the gate-truth the
 * stage and the glyph read: a gate waiting on somebody else is still a gate.
 */
export function isWaitingOnViewer(item: WorkItem, viewerUserId: string | null): boolean {
  return (
    isNeedsYou(item) && viewerUserId != null && item.currentGateSupervisorUserId === viewerUserId
  );
}

/**
 * Who an open gate waits on, as the reader reads it: "you" when it is
 * addressed to them, else the person it names. A person scanning their own
 * queue should not have to recognise their own name in a list.
 */
export function waitingOnWhom(item: WorkItem, viewerUserId: string | null): string {
  if (isWaitingOnViewer(item, viewerUserId)) return "you";
  return item.currentGateSupervisorName?.trim() || "a person";
}

export const PIPELINE_STAGES = WORK_PIPELINE_STAGES;

export type PipelineStage = WorkPipelineStage;

/** The word a section head, a stage chip and a row's state line all print. */
export const PIPELINE_STAGE_LABEL: Record<PipelineStage, string> = {
  proposed: "Proposed",
  plan: "Plan",
  build: "Build",
  review: "Review",
  qa: "QA",
  done: "Done",
};

/**
 * The stage word for a bare `tickets.phase`, when there is no item to read
 * (a tracker search row names only the phase of the work item carrying it).
 * Mirrors `pipelineStageOf`'s phase switch with no PR and no gate: executing
 * and needs_input read Build, the terminal phases read Done, and a phase this
 * build does not know reads Build, as `pipelineStageOf` does.
 */
export function phaseStageLabel(phase: string): string {
  switch (phase) {
    case "proposed":
      return PIPELINE_STAGE_LABEL.proposed;
    case "accepted":
    case "planning":
    case "awaiting_execution_approval":
      return PIPELINE_STAGE_LABEL.plan;
    case "final_review":
      return PIPELINE_STAGE_LABEL.review;
    case "done":
    case "dismissed":
    case "split":
      return PIPELINE_STAGE_LABEL.done;
    default:
      return PIPELINE_STAGE_LABEL.build;
  }
}

/**
 * The CRAFT that owns each stage. `done` is owned by nobody.
 *
 * Crafts, not slugs: the row resolves its owner through the worker directory
 * (`WorkerLookup.byCraft`), which serves the same `craft` word the backend
 * registry defines for each Worker (`WorkerSummary.craft`). A workspace may
 * rename a Worker, and the registry may swap which slug carries a craft; the
 * list keeps naming the right one because the craft word is the contract,
 * the way the backend resolves its builder and reviewer from the registry
 * rather than from a literal.
 */
export const PIPELINE_STAGE_CRAFT: Record<PipelineStage, string | null> = {
  proposed: "Product Manager",
  // Reeve owns the plan line (ADR-0049); Wright builds.
  plan: "Staff Engineer",
  build: "Builder",
  review: "Reviewer",
  qa: "QA",
  done: null,
};

/**
 * Fletcher's environment-setup requests are plumbing, not work: they build the
 * sandbox a later QA run uses, carry no pull request, and answer nothing a
 * manager asked. They stay out of the list entirely.
 */
export function isEnvironmentSetupItem(item: WorkItem): boolean {
  return (
    item.kind === "review" &&
    item.personaSlug === "fletcher" &&
    !item.prUrl &&
    item.title.startsWith("Environment setup:")
  );
}

/**
 * WHAT THE ROW IS DOING — five states, each one a fact the item can prove:
 *
 *   waiting  an OPEN gate ({@link isNeedsYou}, gate-truth). You owe it a move.
 *   working  a run is live right now.
 *   stopped  the last run ended badly and nothing replaced it. Nobody is
 *            working and nobody has been asked: the row is dead until a person
 *            or a rhythm picks it back up, and "queued" would hide that.
 *   settled  terminal.
 *   queued   none of the above. Honestly waiting its turn.
 *
 * Precedence is the reader's, not the machine's: an open gate outranks
 * everything (it is the one state that costs the reader something), then a
 * terminal phase (a finished item is not "working" because a pointer went
 * stale), then the live run, then the failure.
 */
export type RowState = "waiting" | "working" | "stopped" | "queued" | "settled";

/** Run statuses that mean the run ENDED without finishing its work. */
export const STOPPED_RUN_STATUSES: ReadonlySet<string> = new Set([
  "failed",
  "partial",
  "dismissed",
]);

/**
 * Review waits that leave a build whose run stopped short with nothing to
 * answer: only a merge is left, or the reviewer's turn comes next. "merge"
 * stays Stopped on purpose: a person still owes an approval, or the pull
 * request is blocked or conflicts, and nothing will move it on its own.
 */
const MOVES_ON_ITS_OWN: ReadonlySet<string> = new Set(["ready", "proctor"]);

function hasLiveRun(item: WorkItem): boolean {
  if (item.currentRunId != null) return true;
  // A proposal's reading pass (the scoping run, ADR-0027) never claims
  // `currentRunId`: the list read's live latest run is its only trace. Without
  // this it read "Proposed · queued" under Working while the case file showed
  // it reading the code (NMT-109, staging 2026-10-23).
  if (item.phase === "proposed" && item.activeRun != null) return true;
  // A planning run is claimed by `latestPlanningRunId` rather than
  // `currentRunId`, so the drafting state needs its own read — but only while
  // the item is still planning AND that run has not already ended.
  return (
    item.phase === "planning" &&
    item.latestPlanningRunId != null &&
    !STOPPED_RUN_STATUSES.has(item.latestRunStatus ?? "")
  );
}

/** The server-computed holder of an accepted item that is not planning (P0.1). */
function acceptedHoldOf(item: WorkItem): NonNullable<WorkItem["acceptedHold"]> | null {
  return item.phase === "accepted" && item.acceptedHold ? item.acceptedHold : null;
}

/**
 * A proposal with no ask and no run: a below-floor bet the lane parked without
 * a gate, or a thin mint whose grounding never landed. Nobody was asked and
 * nothing will start it, so it is not waiting its turn: only a person opening
 * it (Start plan) moves it. A thin mint whose grounding pass is still pending
 * is the machine's own wait and stays queued.
 */
function isParkedProposal(item: WorkItem): boolean {
  if (item.phase !== "proposed" || item.currentGateId != null) return false;
  const scoping = item.sourceRef?.scoping;
  return !(isProposalScopingRef(scoping) && scoping.status === "pending");
}

export function rowStateOf(item: WorkItem): RowState {
  if (isNeedsYou(item)) return "waiting";
  // A split whose tickets have not all merged is live, not closed (YAG-1111):
  // its tickets are the ones moving, and it waits on them.
  if (liveSplitOf(item)) return "queued";
  if (item.phase === "done" || item.phase === "dismissed" || item.phase === "split")
    return "settled";
  if (hasLiveRun(item)) return "working";
  // A build that stopped short, whose pull request a person then took out of
  // draft and GitHub approved, is not stopped: only the merge is left
  // (staging, 2026-10-21, it sat under Stopped beside a footer that said
  // "Merge it on GitHub"). Nor is one whose pull request waits on Proctor's
  // review: the next move is the reviewer's, not a person's (NEARM-495, PR #84,
  // staging 2026-10-22, read "Review · stopped" under Stopped).
  // Nothing reviews or merges a pull request that closed, whatever the review
  // record last read of it (COREX-1291, prod 2026-10-27).
  // A proposal's one run is its reading pass. Any other latest run is the plan
  // or build from before a person moved it back to Proposed, and its stop is
  // not the proposal's: it reads as a proposal again (YAG-1098).
  const priorWork = item.phase === "proposed" && item.latestRunPurpose !== "scoping";
  if (STOPPED_RUN_STATUSES.has(item.latestRunStatus ?? "") && !priorWork) {
    if (item.latestRunStopReason === PR_CLOSED_UNMERGED_STOP_REASON) return "stopped";
    return MOVES_ON_ITS_OWN.has(reviewWaitOf(reviewWaitFactsOf(item)) ?? "") ? "queued" : "stopped";
  }
  if (isFletcherRequestItem(item) && item.latestRunStatus === "complete") return "settled";
  return "queued";
}

/**
 * WHAT A TICKET IN REVIEW WAITS ON, when nothing is running. One rule for the
 * row (`rowStateLine`), the case file's pill (`workLifecycle`) and the sheet's
 * Proctor line (`ProctorStatus`), so the three cannot disagree. It used to be
 * one fallback, "waiting on the next push", printed over a review still
 * queued, a pull request approved and ready to merge, and an ask waiting on a
 * person (staging, 2026-10-21).
 *
 *   person   an ask is open on the ticket
 *   push     findings a fix round will address are open: the builder owes a push
 *   proctor  the reviewer has not reviewed this head yet (draft or not)
 *   draft    the pull request is still a draft
 *   ready    GitHub approved it and it merges cleanly: only the merge is left
 *   merge    reviewed and out of draft: a person approves or merges it
 *
 * Null when the facts do not say (an older server, a pull request the review
 * record does not watch): the caller says only what it knows.
 */
export type ReviewWait = keyof typeof REVIEW_WAIT_WORDS;

export interface ReviewWaitFacts {
  openAsk?: boolean;
  findingsOpen?: boolean;
  draft?: boolean | null;
  /** `code_review_pr_state.agent_review_state`. */
  proctorState?: string | null;
  reviewDecision?: string | null;
  mergeableState?: string | null;
}

const PROCTOR_PENDING = new Set(["pending_review", "queued", "reviewing"]);
const PROCTOR_REVIEWED = new Set(["comments_posted", "approved", "approval_advised"]);
/** GitHub's `mergeable_state` values a merge goes through on (`unstable`: a check that is not required failed). */
const MERGES_CLEANLY = new Set(["clean", "has_hooks", "unstable"]);

export function reviewWaitOf(facts: ReviewWaitFacts): ReviewWait | null {
  if (facts.openAsk) return "person";
  if (facts.findingsOpen) return "push";
  // A Team's pull request opens as a draft and Proctor's approval takes it out
  // of draft, so a review still owed is the truer fact than the draft flag.
  if (facts.proctorState && PROCTOR_PENDING.has(facts.proctorState)) return "proctor";
  if (facts.draft === true) return "draft";
  if (facts.reviewDecision === "APPROVED" && MERGES_CLEANLY.has(facts.mergeableState ?? ""))
    return "ready";
  if (facts.draft === false && facts.reviewDecision !== "CHANGES_REQUESTED") {
    if (facts.reviewDecision === "APPROVED" || facts.reviewDecision === "REVIEW_REQUIRED")
      return "merge";
    if (facts.proctorState && PROCTOR_REVIEWED.has(facts.proctorState)) return "merge";
  }
  return null;
}

/** {@link ReviewWaitFacts} off a list item: its open gate and the pull request the list joined. */
export function reviewWaitFactsOf(item: WorkItem): ReviewWaitFacts {
  const pr = item.pullRequest ?? null;
  return {
    openAsk: isNeedsYou(item),
    // The reviewer left findings open (a review case's own verdict says the
    // same): a fix round addresses them, so the builder owes a push.
    findingsOpen:
      pr?.proctorState === "needs_follow_up" ||
      (item.kind === "review" && item.stageVerdict === "failed"),
    draft: pr?.draft ?? null,
    proctorState: pr?.proctorState ?? null,
    reviewDecision: pr?.reviewDecision ?? null,
    mergeableState: pr?.mergeableState ?? null,
  };
}

/** The sentence for a wait, as the pill and the Proctor line print it. */
export function reviewWaitWords(wait: ReviewWait | null): string {
  return wait ? REVIEW_WAIT_WORDS[wait] : REVIEW_WAIT_UNKNOWN;
}

/**
 * Approved on GitHub, out of draft, merging cleanly, with no ask open and
 * nothing running: the queue prints it under Ready to merge, as it does the
 * PR watcher's "ready" ask.
 */
export function isApprovedReadyToMerge(item: WorkItem): boolean {
  return (
    pipelineStageOf(item) === "review" &&
    rowStateOf(item) === "queued" &&
    reviewWaitOf(reviewWaitFactsOf(item)) === "ready"
  );
}

/**
 * THE GLYPH'S TWO FACTS, from the row's two: where the work stands
 * ({@link pipelineStageOf}) and what is happening to it ({@link rowStateOf}).
 *
 * It is a translation, not a third vocabulary: every mode is one row state
 * read out loud. The one that is not one-to-one is `queued` — nobody is
 * working and nobody has been asked, so the glyph prints the same inked record
 * a stage already passed does. A ring there would claim one of the two.
 */
export function stagePieOf(item: WorkItem): { stage: PipelineStage; mode: StagePieMode } {
  const stage = pipelineStageOf(item);
  const state = rowStateOf(item);
  // The outcome speaks first: shipped work is green, canceled work is gray,
  // and a review or QA verdict that failed is red on that wedge until a new
  // run picks the item back up.
  const outcome = stageOutcomeOf(item);
  // A live split's ring is the ring of the ticket its row names (YAG-1111).
  const split = isNeedsYou(item) ? null : liveSplitOf(item);
  if (split) return { stage, mode: splitPieMode(split) };
  if (outcome === "shipped") return { stage, mode: "passed" };
  if (outcome === "canceled") return { stage, mode: "canceled" };
  if (outcome === "failed" && state !== "working") return { stage, mode: "failed" };
  switch (state) {
    case "waiting":
      return { stage, mode: "wait" };
    case "working":
      return { stage, mode: "live" };
    case "stopped":
      return { stage, mode: "stop" };
    case "settled":
      // Terminal items answered above through their outcome; what is left is
      // a finished QA check with no failing verdict, which stands in QA.
      return { stage, mode: "past" };
    case "queued": {
      // Accepted with nothing on the Team to plan it, or held by someone
      // other than the machine (P0.1): the amber, still ring. A wait for a
      // free run slot is the machine's own queue, not a stop: it starts on
      // its own, so it keeps the calm ring.
      const hold = acceptedHoldOf(item);
      // A proposal nobody was asked about is the same kind of stop.
      const stopped =
        (hold !== null && hold.kind !== "waiting_turn") ||
        item.planQueueIdle ||
        isParkedProposal(item);
      return { stage, mode: stopped ? "stop" : "past" };
    }
  }
}

/**
 * A machine stop reason in words a manager reads: the shared map
 * (`@yagni/shared` `stopReasonWords`), which the backend's gate titles read
 * too. Anything unmapped reads "stopped" rather than leaking a snake_case
 * token onto the page.
 */
export { stopReasonWords };

/**
 * Why this item's last attempt stopped, in words. The row, the case file's
 * pill and the pane all read this order: the open gate's reason, then the
 * run's own stop code, then its failure reason. A failed supervised build
 * writes a specific stop code but a generic failure reason (`worker_failed`),
 * so the code wins over the failure reason. The one-attempt lane's gate, and
 * its run when the stop opened no gate, name a classified stop cause
 * (`env_install_failed`) where the legacy lane names a stop code: the cause's
 * own words, then, rather than a bare "stopped", as the attempt's timeline
 * says it.
 */
export function itemStopWords(item: WorkItem): string {
  const reason =
    item.currentGateStopReason ?? item.latestRunStopReason ?? item.latestRunFailureReason;
  const words = stopReasonWords(reason);
  if (words !== "stopped" || !isBuildStopCause(reason) || reason === "unknown") return words;
  // A spent round cap says how many blocking findings its review left open (ENG-3028).
  const open = reason === "round_cap" ? (item.latestRunOpenFindingsCount ?? 0) : 0;
  if (open > 0) {
    return `${BUILD_STOP_CAUSE_WORDS[reason]}, with ${open} blocking finding${open === 1 ? "" : "s"} still open`;
  }
  return BUILD_STOP_CAUSE_WORDS[reason];
}

/**
 * Why a planning pass failed, in words. The planner's reasons are machine codes
 * (`scoping_memo_invalid`, prefixed by the scoping read that failed); each one
 * the runner can write gets a sentence, and anything else a plain line rather
 * than the code.
 */
export function planningFailureWords(raw: string | null | undefined): string {
  const reason = (raw ?? "").toLowerCase().replace(/^scoping_/, "");
  switch (reason) {
    case "memo_invalid":
      return "Reading the code twice did not produce a summary the plan could stand on, so no plan was written.";
    case "daily_cap_reached":
      return "The workspace has used today's code reads, so no plan was written.";
    case "budget_exhausted":
      return "The code read reached its spending cap before it finished, so no plan was written.";
    case "request_limit_exhausted":
      return "The code read reached its request limit before it finished, so no plan was written.";
    case "timed_out":
      return "The code read ran out of time before it finished, so no plan was written.";
    case "session_failed":
      return "The sandbox that reads the code could not start, so no plan was written.";
    default:
      return "The planning run stopped before it wrote a plan.";
  }
}

/** "stopped: build timed out", or just "stopped" when nothing named it. */
function stoppedClause(item: WorkItem): string {
  const words = itemStopWords(item);
  return words === "stopped" ? "stopped" : `stopped: ${words}`;
}

/** "It stopped: build timed out.", or "It stopped." when nothing named it: the sheet's sentence. */
export function itemStoppedSentence(item: WorkItem): string {
  return `It ${stoppedClause(item)}.`;
}

/**
 * The open gate's act in words, read through its "needs a person" ask when it
 * carries one: a finished pull request reads "approve Wright's pull request on
 * GitHub", not the `push_fix` act the ask rides on.
 */
export function itemWaitingPhrase(item: WorkItem): string | null {
  return jobActWaitingPhrase(
    item.currentGateAct,
    item.currentGateAgentName,
    gateAskOf(item),
    gateJobLineOf(item),
  );
}

/** The open gate's "needs a person" ask, as the shared copy reads it. */
export function gateAskOf(item: WorkItem): JobActAsk {
  return {
    askKind: item.currentGateAskKind,
    reviewDecision: item.currentGateReviewDecision,
    afterApproval: item.currentGateAfterApproval,
    draft: item.currentGateDraft,
  };
}

/**
 * The Team's work on this ticket is finished and only a person's approval or
 * merge is left: the PR watcher's "ready" ask, or a merge gate. Its Agent has
 * nothing more to change, so the queue prints it apart from the decisions.
 */
export function isReadyToMerge(item: WorkItem): boolean {
  if (rowStateOf(item) !== "waiting") return false;
  return item.currentGateAct === "merge" || item.currentGateAskKind === "ready_for_person";
}

/** The open gate is a stop's: a build that stopped, with the stop's reason on it. */
function isStopGate(item: WorkItem): boolean {
  return (
    rowStateOf(item) === "waiting" &&
    item.currentGateKind === "needs_input" &&
    Boolean(item.currentGateStopReason)
  );
}

/**
 * A stop that reads as a stop first, so the queue prints it with the other
 * stops: one that was ours (decision 5, 2026-10-19: noted on the pull request
 * or the ticket, waiting on nobody), or one written before the classes
 * ("stopped: build failed, waiting on you"). A stop that needs a person is
 * not one of these: it is a decision, and prints under Needs you.
 */
export function isStoppedAsk(item: WorkItem): boolean {
  return isStopGate(item) && item.currentGateStopClass !== "person";
}

/**
 * A stop that was ours and settled with a note: nothing is asked of anyone.
 * The one-attempt lane's stop on our side is not one: it opened an ask, and
 * answering it is how the attempt goes on.
 */
export function isSettledInfraStop(item: WorkItem): boolean {
  return (
    isStopGate(item) &&
    item.currentGateStopClass === "infra" &&
    !isHandOffAskKind(item.currentGateAskKind)
  );
}

/** The one-attempt lane's stop on our side, with the ask it opened. */
function isHandedOffInfraStop(item: WorkItem): boolean {
  return (
    isStopGate(item) &&
    item.currentGateStopClass === "infra" &&
    isHandOffAskKind(item.currentGateAskKind)
  );
}

/**
 * The stage word a row's tag and the sheet's stage button print. The lane's
 * setup ask stands before any stage: the repository has no prepared
 * environment, so it reads Environment, in the stage's own hue.
 */
export function stageLabelOf(item: WorkItem): string {
  if (rowStateOf(item) === "waiting" && item.currentGateAskKind === "run_setup") {
    return "Environment";
  }
  if (!isNeedsYou(item) && liveSplitOf(item)) return SPLIT_LABEL;
  return PIPELINE_STAGE_LABEL[pipelineStageOf(item)];
}

/**
 * "waiting on Priya to accept Bailey's proposal", off the open gate's Job.
 *
 * Null when the gate carries no act, which is every gate a legacy path opened;
 * the caller falls back to the sentence the row always printed. The reader
 * reads as "you" when the gate is addressed to them, because a person scanning
 * their own queue should not have to recognise their own name in a list.
 */
export function waitingOnJobClause(item: WorkItem, viewerUserId: string | null): string | null {
  const phrase = itemWaitingPhrase(item);
  if (!phrase) return null;
  return `waiting on ${waitingOnWhom(item, viewerUserId)} to ${phrase}`;
}

/**
 * The open gate's Job kind and, for a ticket assigned to yagni, who assigned
 * it: what `jobActWaitingPhrase` needs to say "accept what Andres assigned"
 * rather than "accept Bailey's proposal".
 */
export function gateJobLineOf(item: WorkItem): {
  kind: WorkItem["currentGateJobKind"];
  assignedByName: string | null;
} {
  const name = item.sourceRef?.assignedByName;
  return {
    kind: item.currentGateJobKind ?? null,
    assignedByName: typeof name === "string" ? name : null,
  };
}

/**
 * "Wright used the ticket's budget; waiting on you to decide".
 *
 * A fix-round line that spent its cap stamps the review gate with a decision,
 * not an act to approve (robustness decision 2): continue, take over, or stop.
 * Printing "waiting on you to let Wright push the fix" there would ask the
 * wrong question. Read off the gate's own `cappedByPerItemCap`.
 */
export function outOfRoundsRowClause(item: WorkItem, viewerUserId: string | null): string | null {
  if (item.currentGateCapped !== true || item.currentGateAct !== "push_fix") return null;
  return outOfRoundsWaitingClause(
    item.currentGateAgentName ?? null,
    waitingOnWhom(item, viewerUserId),
  );
}

/** Who is working, per stage, when the directory can name them. */
export const WORKING_CLAUSE: Record<PipelineStage, (name: string | null) => string> = {
  proposed: (name) => (name ? `${name} is reading the code` : "the code is being read"),
  plan: (name) => (name ? `${name} is drafting the plan` : "a plan is being drafted"),
  build: (name) => (name ? `${name} is working` : "a run is in motion"),
  review: (name) => (name ? `${name} is reviewing` : "a review is in motion"),
  qa: (name) => (name ? `${name} is testing` : "a check is in motion"),
  done: (name) => (name ? `${name} is working` : "a run is in motion"),
};

/**
 * A build item at work on its own pull request in Review: a fix round. It is
 * the builder's run, not the reviewer's, so the row names Wright fixing, not
 * Proctor reviewing, whatever craft owns the stage (MILD-235, 2026-10-12).
 */
export function isFixingInReview(item: WorkItem): boolean {
  return (
    item.kind !== "review" && pipelineStageOf(item) === "review" && rowStateOf(item) === "working"
  );
}

/** "Wright is fixing the pull request": the working clause of {@link isFixingInReview}. */
export function fixingClause(name: string | null): string {
  return name ? `${name} is fixing the pull request` : "the pull request is being fixed";
}

/**
 * "Wright is fixing 2 asks": a one-attempt lane fix round, when its record
 * counted what the review asked; {@link fixingClause} when it did not.
 */
export function fixingAsksClause(name: string | null, asks: number | null | undefined): string {
  if (typeof asks !== "number" || asks < 1) return fixingClause(name);
  const what = `${asks} ask${asks === 1 ? "" : "s"}`;
  return name ? `${name} is fixing ${what}` : `${what} ${asks === 1 ? "is" : "are"} being fixed`;
}

/**
 * What a Fletcher check found, in the row's words: passed, found problems,
 * blocked, did not finish. Null while no check finished, or on a read that
 * did not carry it.
 */
export const QA_OUTCOME_WORDS: Record<QaOutcome, string> = {
  passed: "passed",
  findings: "found problems",
  blocked: "blocked",
  incomplete: "did not finish",
};

/** A finished check whose outcome the read did not carry. */
export const QA_RESULT_UNKNOWN = "tested";

export function qaResultWords(item: WorkItem): string | null {
  if (item.qaOutcome) return QA_OUTCOME_WORDS[item.qaOutcome];
  if (item.stageVerdict === "failed") return QA_OUTCOME_WORDS.findings;
  return null;
}

/** A check that came back asking a person: it found problems, or it could not test. */
export function qaAsksPerson(item: WorkItem): boolean {
  const outcome = item.qaOutcome ?? (item.stageVerdict === "failed" ? "findings" : null);
  return outcome === "findings" || outcome === "blocked";
}

/**
 * THE ROW'S ONE SENTENCE: where it stands, and what is true there.
 *
 * "Build · Wright is working". "Plan · waiting on you". "Build · stopped: build
 * timed out". Nothing else: no provenance, no description, no schedule. The row
 * has one job, which is to let a reader walk a column of them and see the
 * pipeline, and a second clause on every row is what stopped that working.
 *
 * `workerName` is the STAGE OWNER's display name when the directory knows them,
 * null otherwise, and the copy stays neutral rather than inventing an actor.
 */
export function rowStateLine(
  item: WorkItem,
  workerName: string | null,
  viewerUserId?: string | null,
): string {
  const stage = pipelineStageOf(item);
  const label = stageLabelOf(item);
  const who = workerName ? `${workerName}'s` : "the";
  // A live split (YAG-1111): "Split · 1 of 3 merged · part 2 waiting on you".
  // Its own ask, when it has one, speaks as any ticket's does below.
  const split = isNeedsYou(item) ? null : liveSplitOf(item);
  if (split) return `${label} · ${splitProgressWords(split)}`;

  switch (rowStateOf(item)) {
    case "settled":
      // A dismissed item did not finish. The Done section holds it, but the
      // line refuses to call it shipped.
      if (item.phase === "dismissed") return `${label} · closed, no work was done`;
      // The original of a split whose tickets were not read here: the work
      // moved into the children, so the row says where it went.
      if (item.phase === "split") return `${label} · split into its own tickets`;
      // A split that landed: every one of its tickets merged.
      if (
        item.phase === "done" &&
        item.splitProgress &&
        item.splitProgress.merged >= item.splitProgress.total
      ) {
        return `${label} · all ${item.splitProgress.total} of its tickets merged`;
      }
      // What the check found, never "check complete" (YAG-1079).
      if (stage === "qa") return `${label} · ${qaResultWords(item) ?? QA_RESULT_UNKNOWN}`;
      return label;
    case "waiting": {
      // A build that stopped and then ASKED is two facts, and the reader needs
      // both: why it stopped, and that it is now on them.
      if (item.currentGateAskKind === "run_setup")
        return `${label} · waiting on ${waitingOnWhom(item, viewerUserId ?? null)} to run the repository's setup`;
      if (isHandedOffInfraStop(item)) return `${label} · ${lowerFirst(INFRA_HAND_OFF_SENTENCE)}`;
      if (isSettledInfraStop(item))
        return `${label} · stopped on our side: ${itemStopWords(item)}, nothing is needed from you`;
      if (isStopGate(item))
        return `${label} · ${stoppedClause(item)}, waiting on ${waitingOnWhom(item, viewerUserId ?? null)}`;
      // THE JOB'S SENTENCE (ADR-0044, plan Task 6). When the open gate carries
      // an act, the row says who is waiting on whom and for what: "waiting on
      // Priya to accept Bailey's proposal". It comes from the gate, never from
      // the stage's craft, because the stage says where a ticket IS and the
      // Job says who is holding it up, and the two disagree the moment a line
      // acts outside its stage's craft.
      const cappedClause = outOfRoundsRowClause(item, viewerUserId ?? null);
      if (cappedClause) return `${label} · ${cappedClause}`;
      const jobClause = waitingOnJobClause(item, viewerUserId ?? null);
      if (jobClause) return `${label} · ${jobClause}`;
      // A held verdict is a decision the reviewer has already made and cannot
      // publish; "waiting on you" alone would not say whose it is.
      if (stage === "review") return `${label} · ${who} verdict is held`;
      return `${label} · waiting on ${waitingOnWhom(item, viewerUserId ?? null)}`;
    }
    case "working":
      if (isFixingInReview(item)) return `${label} · ${fixingClause(workerName)}`;
      return `${label} · ${WORKING_CLAUSE[stage](workerName)}`;
    case "stopped":
      return `${label} · ${stoppedClause(item)}`;
    case "queued":
      // "Queued" promises the machine will get to it. A `planning` item that
      // never had a planning run is not in any queue: the phase came from a
      // create or an update, and the queue rhythm starts only `accepted`
      // items. Say so, and the case file offers the Start plan verb.
      // An accepted item's holder is the more exact fact, so it speaks before
      // the idle flag (P0.1): "Plan · Waiting on credits", "Plan · With Priya".
      // A split's ticket waiting on an earlier one says which (ENG-3033).
      const splitWait = splitWaitWords(readSplitPlace(item));
      if (splitWait) return `${label} · ${splitWait}`;
      const hold = acceptedHoldOf(item);
      if (hold) return `${label} · ${acceptedHoldWords(hold)}`;
      if (item.planQueueIdle || isParkedProposal(item)) return `${label} · nothing will start this`;
      if (stageOutcomeOf(item) === "failed")
        return `${label} · ${stage === "qa" ? "check found problems" : "review found problems"}`;
      if (item.phase === "planning" && item.latestPlanningRunId == null)
        return `${label} · not started`;
      // A ticket in Review is not in any queue: a review case stays open until
      // its pull request closes (ADR-0032). It says what it waits on, by the
      // one rule the case file's pill and the sheet's Proctor line read.
      if (stage === "review" && !isFletcherRequestItem(item)) {
        // A build done with findings its own review left open says so before
        // what the review waits on (YAG-1106): "done with 2 findings open,
        // waiting on the review".
        const open = openFindingsClause(item);
        const wait = reviewWaitOf(reviewWaitFactsOf(item));
        // Built ahead, its draft waits on the split's ticket before it (YAG-1112).
        const splitMerge = wait === "draft" ? splitMergeWords(readSplitPlace(item)) : null;
        if (splitMerge) return `${label} · ${open ? `${open}, ` : ""}built, ${splitMerge}`;
        if (wait)
          return `${label} · ${open ? `${open}, ` : ""}${lowerFirst(REVIEW_WAIT_WORDS[wait])}`;
        if (item.kind === "review" && item.latestRunStatus === "complete")
          return `${label} · ${lowerFirst(REVIEW_WAIT_UNKNOWN)}`;
        if (open) return `${label} · ${open}`;
      }
      return `${label} · queued`;
  }
}

/**
 * "done with 2 findings open": the build finished, and the pull request
 * names what Wright's own review could not settle (YAG-1106). Null for a
 * build that left none, and for a stopped run, whose stop names its own
 * (`itemStopWords`).
 */
export function openFindingsClause(item: WorkItem): string | null {
  const open = item.latestRunOpenFindingsCount ?? 0;
  if (open === 0 || item.latestRunStatus !== "complete") return null;
  return `done with ${open} finding${open === 1 ? "" : "s"} open`;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * THE ROW'S SECOND-LINE CLAUSE: {@link rowStateLine} with everything the list
 * already says taken off it.
 *
 * A row sits under a stage heading, so the stage word is said once above it
 * and not again on every line. A row waiting on a person wears the glyph's
 * blue ring and the glass material, and every row in a Proposed section is
 * waiting on the same person, so a bare "waiting on you" is the one clause
 * that says nothing the row has not already said. Everything else stays:
 * "sent back twice; waiting on you" is a counted fact, "stopped: …" is the
 * honesty notice, "Wright is working" names the agent, and "not started" is
 * the case file's Start plan verb before the reader opens it.
 *
 * Null when nothing is left to print, so the line carries the Team and the
 * ticket alone.
 */
export function rowClauseOf(
  item: WorkItem,
  workerName: string | null,
  viewerUserId?: string | null,
): string | null {
  const line = rowStateLine(item, workerName, viewerUserId);
  const label = stageLabelOf(item);
  const clause = line.startsWith(`${label} · `)
    ? line.slice(label.length + 3)
    : line === label
      ? ""
      : line;
  if (!clause || clause === "waiting on you") return null;
  return clause;
}

/**
 * WHEN THE ROW'S CURRENT STATE BEGAN, as an ISO string.
 *
 * One age per row, and it is the age of the STATE rather than of the ticket:
 * "opened 9d ago · touched 2h ago" made a reader do arithmetic to answer the
 * only question they had, which is how long this has been like this. A gate
 * dates from when the ask was raised, a live run from when it started, a
 * stopped run from when it ended. `updatedAt` is the honest fallback when the
 * read did not join the field (list-only joins: `undefined` = not loaded).
 */
export function rowAgeOf(item: WorkItem): string {
  // Time on a row is time in its stage when the list read dated it; the
  // per-state readings below are the fallback for reads that did not.
  if (item.stageEnteredAt) return item.stageEnteredAt;
  switch (rowStateOf(item)) {
    case "waiting":
      return item.currentGateOpenedAt ?? item.updatedAt;
    case "working":
      return item.latestRunStartedAt ?? item.updatedAt;
    case "stopped":
      return item.latestRunFinishedAt ?? item.updatedAt;
    default:
      return item.updatedAt;
  }
}

/**
 * WHEN THE CURRENT STATE BEGAN, for the ticket sheet's "since": the live run's
 * start, the ask's opening, the stop. Unlike {@link rowAgeOf} it never reads
 * time in stage, because a pull request in Review for a week that Proctor
 * started reviewing a minute ago is a minute into the state the sheet names.
 */
export function stateSinceOf(item: WorkItem): string {
  switch (rowStateOf(item)) {
    case "waiting":
      return item.currentGateOpenedAt ?? item.updatedAt;
    case "working":
      return item.activeRun?.startedAt ?? item.latestRunStartedAt ?? item.updatedAt;
    case "stopped":
      return item.latestRunFinishedAt ?? item.updatedAt;
    default:
      return item.updatedAt;
  }
}
